/* eslint-disable react-hooks/exhaustive-deps */
import React, {useEffect, useMemo, useState} from "react"
import styles from "./DocumentForm.module.css"
import {StudentActivity} from "types/activity"
import {Auth} from "types/auth"
import {v4 as uuid} from "uuid"
import {Checkbox, Tooltip} from "antd"
import {FileSelect} from "../FileSelect"
import {BaseButton, BaseInput, BaseLoading, Icon} from "components"
import DepartmentSubunitSelect from "components/DepartmentSubunitSelect"
import StudentActivitySelect from "components/StudentActivitySelect"
import StudentChecklistSelect from "components/StudentChecklistSelect"
import {Modules, useEdularModulesContext} from "@edular/modules"
import {getEnableSubunitsByDepartmentPermission, roundTwoDigits, toastError, toastInfo} from "helpers"
import {useAllDocumentTypes, useModel} from "hooks"
import {useDocumentActions} from "hooks/"
import {PermissionType} from "types/permission"
import UserSelectV3 from "components/UserSelectV3"
import {DocumentType, DocumentTypeItem} from "../DocumentType"
import {DocumentType as DocumentTypeType} from "types/settings/documentType"
import {KlassDropdown} from "components/Select"
import cx from "classnames"
import {widerMenuStyle} from "components/Select/dropdownStyle"
import {groupBy} from "lodash"

const STUDENT_PROFILE_STATE_LABEL = {
    [Auth.StudentState.Student]: "Student",
    [Auth.StudentState.Prospect]: "Applicant",
    [Auth.StudentState.Alumni]: "Alumni",
    [Auth.StudentState.Applicant]: "Applicant",
    [Auth.StudentState.Enrollment]: "Enrollment",
    [Auth.StudentState.TemporaryOut]: "Temporary Out",
    [Auth.StudentState.PermanentOut]: "Permanent Out",
    [Auth.StudentState.NeverAttended]: "Never Attended"
}

const getStudentProfileLabel = (option: any) =>
    `${option.customProfileId || ""} [${STUDENT_PROFILE_STATE_LABEL[option.state] || option.state}]`

const getStudentProfileOptions = (student?: any) => {
    const profiles = student?.profiles || []
    const options = profiles.filter((item) => item.customProfileId)
    const groupedProfile = groupBy(options, "customProfileId") || {}
    const filteredProfile = Object.values(groupedProfile).map((profile) => {
        if (profile.length >= 2) {
            return profile.filter(({state}) => state === Auth.StudentStateType.Student)
        }
        return profile
    })
    const filteredOptions = filteredProfile.flatMap((profile) => profile).filter((profile) => profile?.active)
    return filteredOptions.length ? filteredOptions : profiles
}

const getDefaultStudentProfile = (student?: any) => {
    const options = getStudentProfileOptions(student)
    if (!options.length) {
        return undefined
    }
    return options.find((profile) => profile.id === student?.profileId) ?? options[0]
}

type Document = {
    id: string
    file: File
    student?: any
    studentProfile?: any
    documentName?: string
    description?: string
    departmentSubunit?: any
    isVisibleForUser: boolean
    linkedActivities: StudentActivity[]
    linkedChecklists: any[]
    presetDocumentType?: DocumentTypeType
    notifyStudent: boolean
    isOriginal?: boolean
}

const INITIAL_DOCUMENT_INFO: Document = {
    id: null,
    file: null,
    documentName: "",
    description: "",
    isVisibleForUser: false,
    linkedActivities: [],
    linkedChecklists: [],
    notifyStudent: false,
    isOriginal: false
}

type Props = {
    profileId?: number
    user?: any
    onCancel: () => void
    onDocumentsCreated: () => void
}

export function DocumentForm(props: Props) {
    const {profileId, user, onCancel, onDocumentsCreated} = props
    const [documents, setDocuments] = useState<Document[]>([])
    const {isModuleEnable} = useEdularModulesContext()
    const [errors, setErrors] = useState<Record<string, string[]>>({})
    const model = useModel()
    const {bulkCreate, isBulkCreating} = useDocumentActions()
    const accessibleSubunitIds = useMemo(() => {
        return getEnableSubunitsByDepartmentPermission(PermissionType.Add, model, "Documents")
    }, [])
    const {documentTypes: presetDocumentTypes} = useAllDocumentTypes()
    const [notifyProgress, setNotifyProgress] = useState(false)

    useEffect(() => {
        if (!user) {
            return
        }
        setDocuments((prev) =>
            prev.map((x) => {
                if (x.studentProfile) {
                    return x
                }
                return {...x, studentProfile: getDefaultStudentProfile(user)}
            })
        )
    }, [user])

    const hitLimitPercentage = useMemo(() => {
        const size = documents.reduce((acc, item) => acc + item.file.size, 0)
        return roundTwoDigits(size / (100 * 1024 * 1024)) * 100
    }, [documents.length])

    const handleFieldChange = (id: string, key: string, value: any) => {
        setDocuments((prev) =>
            prev.map((x) => {
                if (x.id === id) {
                    if (key === "student") {
                        return {
                            ...x,
                            student: value,
                            studentProfile: value ? getDefaultStudentProfile(value) : undefined,
                            linkedActivities: [],
                            linkedChecklists: []
                        }
                    }
                    if (key === "studentProfile") {
                        return {...x, studentProfile: value, linkedActivities: [], linkedChecklists: []}
                    }
                    return {...x, [key]: value}
                }
                return x
            })
        )
    }

    const onSelectFiles = (attachedFiles) => {
        setDocuments((prev) => {
            const _newItems: Document[] = attachedFiles.map((x) => {
                return {
                    ...INITIAL_DOCUMENT_INFO,
                    isVisibleForUser: !model.isStaffOrAdmin(),
                    isOriginal: !model.isStaffOrAdmin(),
                    file: x,
                    id: uuid(),
                    studentProfile: user ? getDefaultStudentProfile(user) : undefined
                }
            })
            return [...prev, ..._newItems]
        })
    }

    const removeDocument = (id: string) => {
        setDocuments((prev) => prev.filter((x) => x.id !== id))
    }

    const validateBeforeSubmit = () => {
        let _errors: Record<string, string[]> = {}
        documents.forEach((x) => {
            let error: string[] = []
            if (x.documentName.trim() === "") {
                error.push("documentName")
            }
            if (!profileId && !x.student) {
                error.push("student")
            }
            if (((!profileId && x.student) || profileId) && !x.studentProfile) {
                error.push("studentProfile")
            }
            if (!x.departmentSubunit) {
                error.push("departmentSubunit")
            }
            if (error.length > 0) {
                _errors[x.id] = error
            }
        })
        setErrors(_errors)

        return Object.keys(_errors).length > 0
    }

    const onSubmit = async () => {
        if (validateBeforeSubmit()) {
            toastError("Please input required information")
            return
        }
        const formData = new FormData()
        documents.forEach((x) => {
            const {
                file,
                student,
                studentProfile,
                departmentSubunit,
                documentName,
                description,
                isVisibleForUser,
                linkedActivities,
                linkedChecklists,
                id,
                notifyStudent,
                isOriginal
            } = x
            const fileExtension = file.name.split(".").pop()
            const payload = {
                ownerUserProfileId: studentProfile?.id ?? profileId ?? student?.profileId,
                ownerUserId: student?.id,
                departmentSubunitId: departmentSubunit.subunitId,
                isVisibleForUser,
                isOriginal,
                name: documentName,
                description,
                id,
                departmentId: departmentSubunit.departmentId,
                studentChecklistItemIds: (linkedChecklists || []).map((item) => item.studentChecklistItemId),
                studentActivityIds: (linkedActivities || []).map((item) => item.studentActivityId),
                notifyStudent
            }
            formData.append(`document`, file, `${id}.${fileExtension}`)
            formData.append(`data`, JSON.stringify(payload))
        })
        if (notifyProgress) {
            formData.append(`notifyProgress`, "true")
        }
        bulkCreate(formData, {
            onSuccess: onDocumentsCreated
        })
    }

    const isEnableActivity = isModuleEnable(Modules.ActivitiesAndFollowups)
    const gridTemplateColumns = useMemo(() => {
        let frs: number[] = []
        if (profileId) {
            if (model.isStaffOrAdmin()) {
                if (isEnableActivity) {
                    // cols: file, enrollment, name, description, department, visible, notify, isOriginal, activities, checklists, action
                    frs = [5, 3.5, 5, 8, 4.5, 2, 2, 2, 2, 2, 0.5]
                } else {
                    // cols: file, enrollment, name, description, department, visible, notify, isOriginal, checklists, action
                    frs = [6, 3.5, 5, 8, 4, 2, 2, 2, 2, 0.5]
                }
            } else {
                if (isEnableActivity) {
                    // cols: file, enrollment, name, description, department, activities, checklists, action
                    frs = [5, 3.5, 5, 8, 4.5, 2, 2, 0.5]
                } else {
                    // cols: file, enrollment, name, description, department, checklists, action
                    frs = [6, 3.5, 5, 8, 4, 2, 0.5]
                }
            }
        } else {
            if (isEnableActivity) {
                // cols: file, student, enrollment, name, description, department, visible, notify, isOriginal, activities, checklists, action
                frs = [5, 3.5, 3.5, 4, 6, 3, 2, 2, 2, 2, 2, 0.5]
            } else {
                // cols: file, student, enrollment, name, description, department, visible, notify, isOriginal, checklists, action
                frs = [5, 3.5, 3.5, 4, 6, 4, 2, 2, 2, 2, 0.5]
            }
        }

        return frs.map((x) => `minmax(0, ${x}fr)`).join(" ")
    }, [profileId, isEnableActivity])

    const onApplyDocumentType = (documentType: DocumentTypeItem) => {
        if (documents.length === 0) {
            toastInfo("No document to apply template")
            return
        }
        const _documents = documents.map((x) => {
            return {
                ...x,
                documentName: documentType.documentName,
                description: documentType.description,
                departmentSubunit: documentType.departmentSubunit,
                isVisibleForUser: documentType.isVisibleForUser,
                isOriginal: documentType.isOriginal,
                notifyStudent: documentType.notifyStudent,
                presetDocumentType: documentType.presetDocumentType
            }
        })
        setDocuments(_documents)
        toastInfo("Template applied")
    }

    return (
        <div className={styles.container}>
            <div className={styles.formTitle}>Upload documents</div>
            <FileSelect onSelectFiles={onSelectFiles} />
            <DocumentType accessibleSubunitIds={accessibleSubunitIds} onApply={onApplyDocumentType} />
            <div className={styles.tableContainer}>
                <div className={styles.rowHeader} style={{gridTemplateColumns}}>
                    <span className={styles.headerTitle}>File</span>
                    {!profileId && (
                        <span className={styles.headerTitle}>
                            Student<span className={styles.star}>*</span>
                        </span>
                    )}
                    <span className={styles.headerTitle}>
                        Enrollment ID<span className={styles.star}>*</span>
                    </span>
                    <span className={styles.headerTitle}>
                        Document Name<span className={styles.star}>*</span>
                    </span>
                    <span className={styles.headerTitle}>Description</span>
                    <span className={styles.headerTitle}>
                        Department<span className={styles.star}>*</span>
                    </span>
                    {model.isStaffOrAdmin() && (
                        <>
                            <span className={cx(styles.headerTitle, styles.headerCenter)}>Visible to user</span>
                            <span className={cx(styles.headerTitle, styles.headerCenter)}>Notify student</span>
                            <span className={cx(styles.headerTitle, styles.headerCenter)}>Original</span>
                        </>
                    )}
                    {isEnableActivity && <span className={styles.headerTitle}>Activities</span>}
                    <span className={styles.headerTitle}>Checklists</span>
                </div>
                {documents.map((document, index) => {
                    const profileIdSelected = document.studentProfile?.id ?? profileId ?? document.student?.profileId
                    const profileOptionsUser = document.student || user
                    return (
                        <div key={index} className={styles.row} style={{gridTemplateColumns}}>
                            <Tooltip title={document.file?.name}>
                                <span className={styles.docName}>{document.file?.name}</span>
                            </Tooltip>
                            {!profileId && (
                                <div className={styles.col}>
                                    <UserSelectV3
                                        type={[Auth.UserProfileType.Student]}
                                        value={document.student}
                                        onChange={(v) => handleFieldChange(document.id, "student", v)}
                                        placeholder="student"
                                        error={(errors[document.id] || []).includes("student")}
                                        stylesCustom={{...widerMenuStyle}}
                                    />
                                </div>
                            )}
                            <div className={styles.col}>
                                {profileOptionsUser && (
                                    <KlassDropdown
                                        options={getStudentProfileOptions(profileOptionsUser)}
                                        value={document.studentProfile}
                                        valueKey="id"
                                        getOptionLabel={getStudentProfileLabel}
                                        onChange={(v) => handleFieldChange(document.id, "studentProfile", v)}
                                        placeholder="enrollment id"
                                        error={(errors[document.id] || []).includes("studentProfile")}
                                        stylesCustom={{...widerMenuStyle}}
                                    />
                                )}
                            </div>
                            <div className={styles.col}>
                                <KlassDropdown
                                    key={document.presetDocumentType?.name}
                                    creatable
                                    placeholder="Select or enter name"
                                    formatCreateLabel={(inputValue) => `Use "${inputValue}"`}
                                    isValidNewOption={(inputValue: string) => !!inputValue}
                                    getNewOptionData={(inputValue: string) => ({
                                        documentTypeId: 0,
                                        name: inputValue
                                    })}
                                    options={presetDocumentTypes}
                                    valueKey="documentTypeId"
                                    labelKey="name"
                                    value={
                                        document.presetDocumentType
                                            ? presetDocumentTypes.find(
                                                  (x) => x.documentTypeId === document.presetDocumentType.documentTypeId
                                              ) ?? {
                                                  documentTypeId: 0,
                                                  name: document.presetDocumentType.name
                                              }
                                            : undefined
                                    }
                                    onChange={(option: DocumentTypeType) => {
                                        let _document: Document = {
                                            ...document,
                                            presetDocumentType: option,
                                            documentName: option?.name
                                        }
                                        if (option && option.documentTypeId !== 0) {
                                            _document.description = option.description
                                            _document.departmentSubunit = option.departmentSubunit
                                        }
                                        setDocuments((prev) =>
                                            prev.map((x) => {
                                                if (x.id === document.id) {
                                                    return _document
                                                }
                                                return x
                                            })
                                        )
                                    }}
                                    error={(errors[document.id] || []).includes("documentName")}
                                    stylesCustom={{...widerMenuStyle}}
                                />
                            </div>
                            <div className={styles.col}>
                                <BaseInput
                                    placeholder="description"
                                    value={document.description}
                                    onChange={(v) => handleFieldChange(document.id, "description", v)}
                                    error={(errors[document.id] || []).includes("description")}
                                />
                            </div>
                            <div className={styles.col}>
                                <DepartmentSubunitSelect
                                    subunitIds={accessibleSubunitIds}
                                    placeholder="department"
                                    value={document.departmentSubunit}
                                    onChange={(v) => handleFieldChange(document.id, "departmentSubunit", v)}
                                    error={(errors[document.id] || []).includes("departmentSubunit")}
                                    stylesCustom={{...widerMenuStyle}}
                                />
                            </div>
                            {model.isStaffOrAdmin() && (
                                <>
                                    <div className={styles.checkbox}>
                                        <Checkbox
                                            checked={document.isVisibleForUser}
                                            onChange={(v) =>
                                                handleFieldChange(document.id, "isVisibleForUser", v.target.checked)
                                            }
                                        />
                                    </div>
                                    <div className={styles.checkbox}>
                                        <Checkbox
                                            checked={document.notifyStudent}
                                            onChange={(v) =>
                                                handleFieldChange(document.id, "notifyStudent", v.target.checked)
                                            }
                                        />
                                    </div>
                                    <div className={styles.checkbox}>
                                        <Checkbox
                                            checked={document.isOriginal}
                                            onChange={(v) =>
                                                handleFieldChange(document.id, "isOriginal", v.target.checked)
                                            }
                                        />
                                    </div>
                                </>
                            )}
                            {isModuleEnable(Modules.ActivitiesAndFollowups) && (
                                <div className={styles.col}>
                                    {profileIdSelected && (
                                        <StudentActivitySelect
                                            key={profileIdSelected}
                                            profileId={profileIdSelected}
                                            value={document.linkedActivities}
                                            onChange={(v) => handleFieldChange(document.id, "linkedActivities", v)}
                                            isMulti
                                            stylesCustom={{...widerMenuStyle}}
                                        />
                                    )}
                                </div>
                            )}
                            <div className={styles.col}>
                                {profileIdSelected && (
                                    <StudentChecklistSelect
                                        key={profileIdSelected}
                                        profileIds={[profileIdSelected]}
                                        value={document.linkedChecklists}
                                        onChange={(v) => handleFieldChange(document.id, "linkedChecklists", v)}
                                        isMulti
                                        stylesCustom={{...widerMenuStyle}}
                                    />
                                )}
                            </div>
                            <div className={styles.action}>
                                <Tooltip title="Remove document">
                                    <div className={styles.removeWrap} onClick={() => removeDocument(document.id)}>
                                        <Icon icon="DELETE" className={styles.removeIcon} />
                                    </div>
                                </Tooltip>
                            </div>
                        </div>
                    )
                })}
            </div>
            <div className={styles.actionWrap}>
                <div className={styles.sizeDescriptionWrap}>
                    <span className={styles.sizeDescription}>
                        Upload Limit: 100MB (You're at{" "}
                        <span
                            className={cx(styles.limit, {
                                [styles.hitLimit]: hitLimitPercentage > 100
                            })}>{`${hitLimitPercentage}%`}</span>
                        )
                    </span>
                    {model.isStaffOrAdmin() && (
                        <Checkbox
                            checked={notifyProgress}
                            onChange={(event) => setNotifyProgress(event.target.checked)}
                            className={styles.notifyCheckbox}>
                            Notify me when the upload is processed (Useful for multiple files)
                        </Checkbox>
                    )}
                </div>
                <div className={styles.doneBtn}>
                    <BaseButton title="Cancel" onClick={onCancel} variant="secondary" />
                    <BaseButton
                        title="Submit"
                        onClick={onSubmit}
                        loading={isBulkCreating}
                        disabled={documents.length <= 0}
                    />
                </div>
            </div>
            <BaseLoading isShow={isBulkCreating} />
        </div>
    )
}
